function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve())
  })
}

/** Wait until Vite's main CSS bundle (or dev-injected styles) is applied. */
function waitForAppStylesheets(): Promise<void> {
  const cssLinks = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')).filter(
    (link) => {
      const href = link.href || link.getAttribute('href') || ''
      return href.includes('/assets/index') || href.includes('/assets/index-')
    },
  )

  if (cssLinks.length === 0) {
    // Dev: CSS is injected as <style> when the entry module runs (already imported).
    return Promise.resolve()
  }

  const pending = cssLinks.filter((link) => {
    try {
      return link.sheet === null
    } catch {
      return true
    }
  })

  if (pending.length === 0) {
    return Promise.resolve()
  }

  return Promise.all(
    pending.map(
      (link) =>
        new Promise<void>((resolve) => {
          link.addEventListener('load', () => resolve(), { once: true })
          link.addEventListener('error', () => resolve(), { once: true })
        }),
    ),
  ).then(() => undefined)
}

/**
 * Reveal the React app only after styles are ready and the first styled frame has painted.
 * Keeps the boot splash on top until then to avoid any FOUC.
 */
export async function revealAppWhenReady(rootElement: HTMLElement): Promise<void> {
  await waitForAppStylesheets()
  await nextFrame()
  await nextFrame()

  rootElement.classList.add('app-visible')

  // Paint styled app underneath the splash before fading the splash away.
  await nextFrame()

  const splash = document.getElementById('app-boot-splash')
  if (!splash) return

  splash.classList.add('boot-splash-exit')

  await new Promise<void>((resolve) => {
    const done = () => resolve()
    splash.addEventListener('transitionend', done, { once: true })
    window.setTimeout(done, 300)
  })

  splash.setAttribute('hidden', '')
}
